import React from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod/v3';
import { zodResolver } from '@hookform/resolvers/zod';
import { differenceInYears, isValid, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
export const getSocialSecurityNumberRegex = () => /^(?:\d{3}-?\d{2}-?\d{4}|\d{4})$/;

export const validateISODateFormat = (val) => {
  return val ? isValid(parseISO(val)) : true;
};

export const validateMinimumAge = (val) => {
  if (!val) return true;
  const date = parseISO(val);
  if (!isValid(date)) return true;
  return differenceInYears(new Date(), date) >= 18;
};

const useCreateApplicationForm = (errorConfig = {}) => {
  const { t } = useTranslation();

  const schema = React.useMemo(() => {
    return z.object({
      // salutation: z.string().min(1,{ message: t('common.requiredSalutation') }),
      firstName: z.string().min(1, { message: t('common.requiredFieldFirstName') }),
      lastName: z.string().min(1, { message: t('common.requiredFieldLastName') }),
      address: z.string().min(1, { message: t('common.requiredFieldStreet') }),
      city: z.string().min(1, { message: t('common.requiredFieldCity') }),
      state: z.string().min(1, { message: t('common.requiredFieldState') }),
      zip: z.string().min(1, { message: t('common.requiredFieldPostalCode') }),
      // country: z.string().min(1,{ message: t('common.requiredFieldCountry') }),
      email: z
        .string()
        .min(1, { message: t('common.requiredFieldEmail') })
        .email({ message: t('common.invalidFormatEmail') }),
      socialSecurityNumber: z
        .string()
        .min(1, { message: t('common.requiredFieldSSN') })
        .refine(
          (val) => {
            const regex = getSocialSecurityNumberRegex();
            return regex.test(val);
          },
          { message: t('fortiva.formlabel.invalidFormatSSN') }
        ),
      phone: z
        .string()
        .min(1, { message: t('common.requiredFieldPhone') })
        .regex(/^\d{10}$/, { message: 'Please provide a valid phone number with 10 digits' }),
      dateOfBirth: z
        .any()
        .refine(validateISODateFormat, { message: t('common.invalidDateFormat') })
        .refine(validateMinimumAge, {
          message: 'You must be at least 18 years old to apply',
        }),
      annualIncome: z
        .string()
        .transform(parseInt)
        .refine((val) => val >= 1, { message: t('common.requiredFieldAnnualIncome') }),
      terms: z.boolean().refine((val) => val, { message: t('common.requiredFieldTerms') }),
    });
  }, [t]);

  const form = useForm({
    resolver: zodResolver(schema),
  });

  const { setError } = form;

  // Set initial errors based on errorConfig
  React.useEffect(() => {
    if (errorConfig.affectedFields && Array.isArray(errorConfig.affectedFields)) {
      errorConfig.affectedFields.forEach((fieldName) => {
        // Set a generic error message or use a custom one from errorConfig
        const errorMessage = errorConfig.message || t('common.fieldHasError');
        setError(fieldName, {
          type: 'manual',
          message: errorMessage,
        });
      });
    }
  }, [errorConfig, setError, t]);

  return form;
};

export default useCreateApplicationForm;
